import React, { useEffect, useRef } from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import composeRefs from "@seznam/compose-react-refs";

import {
  Tabs as ShadcnTabs,
  TabsContent as ShadcnTabsContent,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
} from "../shadcn/core/tabs";

// Helper function to scroll active tab into view
const scrollActiveTabIntoView = (
  tab: HTMLButtonElement | null,
  container: HTMLDivElement | null,
) => {
  if (!tab || !container) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const tabRect = tab.getBoundingClientRect();

  const isNotFullyVisible =
    tabRect.left < containerRect.left || tabRect.right > containerRect.right;

  if (isNotFullyVisible) {
    const scrollLeft =
      tab.offsetLeft - container.offsetWidth / 2 + tab.offsetWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: "smooth",
    });
  }
};

type TabsTriggerOption = {
  value: string;
  label: React.ReactNode;
} & Omit<TabsTriggerProps, "value" | "children">;

type TabsContentOption = {
  value: string;
  tabContent: React.ReactNode;
} & Omit<TabsContentProps, "value" | "children">;

type TabsProps = {
  value: string;
  onValueChange: (value: string) => void;
  defaultValue: string;
  tabsOptions: TabsTriggerOption[];
  contentOptions?: TabsContentOption[];
  layoutClassName?: string;
  UNSAFE_className?: string;
  size?: "sm" | "base";
} & Omit<TabsRootProps, "className">;

const Tabs = ({
  value,
  onValueChange,
  defaultValue,
  tabsOptions,
  contentOptions,
  layoutClassName,
  UNSAFE_className,
  size = "sm",
}: TabsProps) => {
  const tabsListRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  return (
    <TabsRoot
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      layoutClassName={layoutClassName}
      UNSAFE_className={UNSAFE_className}
    >
      <TabsList size={size} ref={tabsListRef}>
        {tabsOptions.map((tab) => {
          const { value: tabValue, label, ...rest } = tab;

          return (
            <TabsTrigger
              ref={value === tabValue ? activeTabRef : undefined}
              size={size}
              key={tabValue}
              value={tabValue}
              {...rest}
            >
              {label}
            </TabsTrigger>
          );
        })}
      </TabsList>
      <div className="min-h-0 flex-1">
        {contentOptions?.map((content) => {
          const { value, tabContent, ...rest } = content;

          return (
            <TabsContent
              key={value}
              value={value}
              UNSAFE_className="pt-4"
              {...rest}
            >
              {tabContent}
            </TabsContent>
          );
        })}
      </div>
    </TabsRoot>
  );
};

interface TabsRootProps
  extends Omit<React.ComponentPropsWithoutRef<typeof ShadcnTabs>, "className"> {
  value?: string;
  onValueChange?: (value: string) => void;
  layoutClassName?: string;
  UNSAFE_className?: string;
}

const TabsRoot = React.forwardRef<HTMLDivElement, TabsRootProps>(
  ({ layoutClassName, UNSAFE_className, ...props }, ref) => {
    return (
      <ShadcnTabs
        ref={ref}
        className={twMerge(
          "flex h-full flex-col",
          layoutClassName,
          UNSAFE_className,
        )}
        {...props}
      />
    );
  },
);
TabsRoot.displayName = "TabsRoot";

interface TabsListProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnTabsList>,
    "className"
  > {
  layoutClassName?: string;
  UNSAFE_className?: string;
  size?: "sm" | "base";
}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  (
    { layoutClassName, UNSAFE_className, children, size = "sm", ...props },
    ref,
  ) => {
    return (
      <ShadcnTabsList
        ref={ref}
        className={twMerge(
          "flex h-[unset] w-full flex-none items-end justify-start rounded-none border-none bg-transparent p-0",
          "overflow-x-scroll p-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          layoutClassName,
          UNSAFE_className,
        )}
        {...props}
      >
        <div
          className={twMerge(
            "border-b-0.5 border-border flex flex-1 items-end justify-start rounded-none bg-transparent p-0",
            size === "sm" && "gap-2.5",
            size === "base" && "gap-4",
            layoutClassName,
            UNSAFE_className,
          )}
        >
          {children}
        </div>
      </ShadcnTabsList>
    );
  },
);
TabsList.displayName = "TabsList";

interface TabsTriggerProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnTabsTrigger>,
    "className"
  > {
  size?: "sm" | "base";
  layoutClassName?: string;
  UNSAFE_className?: string;
}

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ layoutClassName, UNSAFE_className, size, value, ...props }, ref) => {
    const triggerRef = useRef<HTMLButtonElement | null>(
      null,
    ) as React.MutableRefObject<HTMLButtonElement | null>;

    useEffect(() => {
      if (!triggerRef.current) {
        return;
      }

      const checkAndScroll = () => {
        if (triggerRef.current) {
          const isActive = triggerRef.current.dataset.state === "active";
          if (isActive) {
            const scrollableParent =
              triggerRef.current.parentElement?.parentElement;

            if (scrollableParent) {
              requestAnimationFrame(() => {
                scrollActiveTabIntoView(
                  triggerRef.current,
                  scrollableParent as HTMLDivElement,
                );
              });
            }
          }
        }
      };

      checkAndScroll();

      const observer = new MutationObserver(checkAndScroll);
      observer.observe(triggerRef.current, {
        attributes: true,
        attributeFilter: ["data-state"],
      });

      return () => {
        observer.disconnect();
      };
    }, []);

    return (
      <ShadcnTabsTrigger
        ref={composeRefs(triggerRef, ref)}
        value={value}
        className={twMerge(
          "text-placeholder data-[state=active]:border-primary data-[state=active]:text-primary relative mb-[-2px] rounded-none border-b-2 border-transparent px-0 transition disabled:pointer-events-auto disabled:cursor-not-allowed data-[state=active]:z-10 data-[state=active]:shadow-none",
          "focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-offset-0 focus-visible:ring-offset-transparent",
          size === "sm" &&
            "data-[state=active]:text-xs font-semibold py-1 text-xs font-medium",
          size === "base" &&
            "data-[state=active]:header-h6 py-1.5 text-sm font-medium",
          layoutClassName,
          UNSAFE_className,
        )}
        {...props}
      />
    );
  },
);
TabsTrigger.displayName = "TabsTrigger";

interface TabsContentProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnTabsContent>,
    "className"
  > {
  layoutClassName?: string;
  UNSAFE_className?: string;
}

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(
  ({ layoutClassName, UNSAFE_className, ...props }, ref) => {
    return (
      <ShadcnTabsContent
        ref={ref}
        className={twMerge(
          "focus-visible:outline-hidden mt-0 h-full overflow-y-auto ring-offset-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "focus-visible:ring-0 focus-visible:ring-inset focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent",
          layoutClassName,
          UNSAFE_className,
        )}
        {...props}
      />
    );
  },
);
TabsContent.displayName = "TabsContent";

/** @deprecated Use Tabs from @replo/design-system/components/tabs/Tabs instead */
const DeprecatedTabs = Tabs;

/** @deprecated Use TabsProps from @replo/design-system/components/tabs/Tabs instead */
type DeprecatedTabsProps = TabsProps;
/** @deprecated Use TabsTriggerOption - no V2 equivalent, build with TabsTrigger directly */
type DeprecatedTabsTriggerOption = TabsTriggerOption;
/** @deprecated Use TabsContentOption - no V2 equivalent, build with TabsContent directly */
type DeprecatedTabsContentOption = TabsContentOption;

/** @public */
export type {
  TabsProps,
  TabsTriggerOption,
  TabsContentOption,
  TabsRootProps,
  TabsListProps,
  TabsTriggerProps,
  TabsContentProps,
  DeprecatedTabsProps,
  DeprecatedTabsTriggerOption,
  DeprecatedTabsContentOption,
};

Tabs.Root = TabsRoot;
Tabs.List = TabsList;
Tabs.Trigger = TabsTrigger;
Tabs.Content = TabsContent;

DeprecatedTabs.Root = TabsRoot;
DeprecatedTabs.List = TabsList;
DeprecatedTabs.Trigger = TabsTrigger;
DeprecatedTabs.Content = TabsContent;

export { DeprecatedTabs };
